import { AGENT_TEXT_CONFIG, DEFAULT_AGENT_TEXT } from "./constants";

export const agentEndpoints: Record<number, string> = {
  //   1: "/press-release",
  //   2: "/social-media",
  //   3: "/email-writer",
  4: "/press-release",
  5: "/brand-review",
  6: "/twitter",
  7: "/localizer",
  8: "",
  9: "",
  10: "",
};

export function getAgentChatUrl(agentId: number) {
  const endpoint = agentEndpoints[agentId];
  if (!endpoint) throw new Error("Unknown agent");
  return endpoint;
}

export function confirmAffirmative(text: string = "") {
  const patterns = ["yes", "ok", "okay", "yep", "sure", "proceed"];
  const lc = text.toLowerCase();

  return patterns.some((word) => lc.includes(word));
}

export const safeParse = (val: any) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
};

export function safeParseAgentContent(value: any) {
  if (!value) return null; // null, undefined, empty string
  if (typeof value === "object") return value; // already parsed
  if (typeof value !== "string") return null;

  try {
    return JSON.parse(value);
  } catch (err) {
    console.error("JSON parse failed:", value);
    return null;
  }
}

export function safeJSONParse(value: any) {
  try {
    if (!value) return null;

    // Already an object → return as is
    if (typeof value === "object") return value;

    // Must be a string to parse
    if (typeof value === "string") {
      // Reject accidental [object Object]
      if (value.trim() === "[object Object]") return null;

      return JSON.parse(value);
    }

    return null;
  } catch (err) {
    return null;
  }
}

export function snakeToTitleCase(str: string): string {
  if (!str) return "";

  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function toTitleCase(str: string): string {
  if (!str) return "";

  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function socialMediaScoreParsing(
  score: number
): "High" | "Medium" | "Low" | "" {
  if (score == null) return "";

  if (score >= 9) return "High";
  if (score >= 6) return "Medium";
  return "Low";
}

export function socialMediaTotalScoreParsing(
  score: number
): "High" | "Medium" | "Low" | "" {
  if (score == null) return "";

  if (score >= 90) return "High";
  if (score >= 60) return "Medium";
  return "Low";
}

export type ScoreLevel = "High" | "Medium" | "Low" | "";

/**
 * Get color for score level badge
 * @param level - Score level (High, Medium, Low)
 * @returns Hex color code
 */
export function getScoreLevelColor(level: ScoreLevel): string {
  switch (level) {
    case "High":
      return "#10b981"; // Green
    case "Medium":
      return "#ffeb3b"; // Yellow
    case "Low":
      return "#ef4444"; // Red
    default:
      return "#9ca3af"; // Gray
  }
}

// Helper function to sanitize text by removing underscores
export const sanitizeText = (text: string | undefined | null): string => {
  if (!text) return "";
  return text.replace(/_/g, " ");
};

// Helper functions to get agent-specific text
export const getAgentSubtitle = (agentId?: string): string => {
  if (!agentId) return DEFAULT_AGENT_TEXT.subtitle;
  return AGENT_TEXT_CONFIG[agentId]?.subtitle || DEFAULT_AGENT_TEXT.subtitle;
};

export const getAgentPromptTitle = (agentId?: string): string => {
  if (!agentId) return DEFAULT_AGENT_TEXT.promptTitle;
  return AGENT_TEXT_CONFIG[agentId]?.promptTitle || DEFAULT_AGENT_TEXT.promptTitle;
};

export const getPlaceholderByAgentId = (agentId?: string): string => {
  if (!agentId) return DEFAULT_AGENT_TEXT.inputPlaceholder;
  return AGENT_TEXT_CONFIG[agentId]?.inputPlaceholder || DEFAULT_AGENT_TEXT.inputPlaceholder;
};

export const formatDate = (isoString: string | undefined): string => {
  if (!isoString) return '';
  const date = new Date(isoString);

  // Get day, month, year
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  // Add ordinal suffix to day
  const ordinalSuffix = (n: number): string => {
    if (n > 3 && n < 21) return 'th'; // 11th, 12th, 13th
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return `${day}${ordinalSuffix(day)} ${month}, ${year}`;
};